import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import BaseModel from '../../BaseModal';
import { authorizedHttpGet } from '../../api/httpCalls';
import urls from '../../api/urls.json';
import Loader from '../Loader';

const ParentDiv = styled.div`
  width: 900px;
  max-width: 90vw;
  max-height: 80vh;
  overflow-y: auto;
  padding: 10px 5px 20px;
`;

const Title = styled.h2`
  margin: 0 0 18px;
  color: #0f172a;
  font-family: 'Gilory-Bold';
`;

const Description = styled.p`
  margin: 0 0 18px;
  color: #475569;
  font-size: 15px;
  line-height: 1.5;
`;

const Section = styled.div`
  margin-top: 18px;
  padding: 18px;
  border: 1px solid #dbe3ef;
  border-radius: 16px;
  background: #f8fbff;
`;

const SectionTitle = styled.h3`
  margin: 0 0 14px;
  color: #12344d;
  font-family: 'Gilory-Semi';
  font-size: 18px;
`;

const StatusCard = styled.div`
  margin-top: 18px;
  padding: 18px;
  border-radius: 16px;
  border: 1px solid ${({ $eligible }) => ($eligible ? '#86d7aa' : '#f3c4c4')};
  background: ${({ $eligible }) => ($eligible ? '#edfdf4' : '#fff5f5')};
`;

const StatusTitle = styled.div`
  color: #0f172a;
  font-family: 'Gilory-Bold';
  font-size: 18px;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  margin-top: 10px;
  padding: 6px 12px;
  border-radius: 999px;
  background: ${({ $eligible }) => ($eligible ? '#d9f7e8' : '#fde2e2')};
  color: ${({ $eligible }) => ($eligible ? '#0f8a4b' : '#b42318')};
  border: 1px solid ${({ $eligible }) => ($eligible ? '#86d7aa' : '#f5b5b5')};
  font-family: 'Gilory-Semi';
  font-size: 14px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 18px;
`;

const Row = styled.div`
  padding: 12px 14px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
`;

const Label = styled.div`
  margin-bottom: 6px;
  color: #64748b;
  font-size: 13px;
`;

const Value = styled.div`
  color: #0f172a;
  font-size: 15px;
  font-family: 'Gilory-Semi';
  word-break: break-word;
  white-space: pre-wrap;
`;

const EmptyState = styled.div`
  padding: 24px;
  border-radius: 16px;
  background: #f8fafc;
  color: #334155;
  text-align: center;
`;

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (typeof value === 'object') {
    const keys = Object.keys(value || {});
    return keys.length > 0 ? JSON.stringify(value, null, 2) : '-';
  }

  const stringValue = String(value);
  const parsedDate = new Date(stringValue);
  if (!Number.isNaN(parsedDate.getTime()) && /[-:T]/.test(stringValue)) {
    return parsedDate.toLocaleString();
  }

  return stringValue;
};

const hasMeaningfulValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return false;
  }

  if (typeof value === 'object') {
    return Object.keys(value).length > 0;
  }

  return true;
};

const primaryFields = [
  ['id', 'MBG Record ID'],
  ['user_id', 'User ID'],
  ['payment_id', 'Payment ID'],
  ['construct_version', 'Construct Version'],
  ['refund_done', 'Refund Done'],
  ['is_indian', 'Indian User'],
  ['cohort', 'Cohort'],
  ['created_at', 'Created At'],
  ['updated_at', 'Updated At']
];

const formatPaymentDisplay = (data) => {
  const paymentId = data?.payment_id;
  const transactionId = data?.transaction_id;

  if (paymentId && transactionId) {
    return `${paymentId}(${transactionId})`;
  }

  return formatValue(paymentId);
};

const renderFieldGrid = (data, fields) => (
  <Grid>
    {fields.map(([key, title]) => (
      <Row key={key}>
        <Label>{title}</Label>
        <Value>{key === 'payment_id' ? formatPaymentDisplay(data) : formatValue(data?.[key])}</Value>
      </Row>
    ))}
  </Grid>
);

const MoneyBackGuaranteeModal = ({ closeModal, userRef }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [mbgInfo, setMbgInfo] = useState(null);
  const [exists, setExists] = useState(false);
  const [refundEligibility, setRefundEligibility] = useState(null);

  useEffect(() => {
    const fetchMoneyBackGuaranteeInfo = async () => {
      setIsLoading(true);
      const response = await authorizedHttpGet(urls.FETCH_MONEY_BACK_GUARANTEE_INFO, { ref: userRef });
      if (response.status && response.data) {
        setExists(Boolean(response.data.exists));
        setMbgInfo(response.data.money_back_guarantee || null);
        setRefundEligibility(response.data.refund_eligibility || null);
      } else {
        setExists(false);
        setMbgInfo(null);
        setRefundEligibility(null);
      }
      setIsLoading(false);
    };

    fetchMoneyBackGuaranteeInfo();
  }, [userRef]);

  return (
    <BaseModel closeModal={closeModal}>
      {isLoading ? <Loader /> : null}
      <ParentDiv>
        <Title>Money Back Guarantee</Title>
        <Description>
          This view shows the MBG record stored for the user. It is meant for support and curation,
          so each block maps directly to what the backend is tracking.
        </Description>
        {!exists ? (
          <EmptyState>No Money Back Guarantee record found for this user.</EmptyState>
        ) : (
          <>
            <Section>
              <SectionTitle>MBG Status</SectionTitle>
              {renderFieldGrid(mbgInfo, primaryFields)}
            </Section>

            {refundEligibility ? (
              <Section>
                <SectionTitle>Refund Eligibility</SectionTitle>
                <StatusCard $eligible={refundEligibility.eligible_for_refund}>
                  <StatusTitle>Support Summary</StatusTitle>
                  <StatusBadge $eligible={refundEligibility.eligible_for_refund}>
                    {refundEligibility.eligible_for_refund ? 'Eligible for refund' : 'Not eligible for refund'}
                  </StatusBadge>
                  <Description style={{ marginTop: '12px', marginBottom: 0 }}>
                    {refundEligibility.summary}
                  </Description>
                </StatusCard>

                <Grid style={{ marginTop: '14px' }}>
                  <Row>
                    <Label>Claim Window</Label>
                    <Value>
                      {refundEligibility.requirements?.claim_window?.passed ? 'Open' : 'Closed'}
                      {' · '}
                      {formatValue(refundEligibility.requirements?.claim_window?.start_date)}
                      {' → '}
                      {formatValue(refundEligibility.requirements?.claim_window?.end_date)}
                    </Value>
                  </Row>
                  <Row>
                    <Label>Likes Sent</Label>
                    <Value>
                      {formatValue(refundEligibility.requirements?.likes_sent?.current)}
                      {' / '}
                      {formatValue(refundEligibility.requirements?.likes_sent?.required)}
                    </Value>
                  </Row>
                  <Row>
                    <Label>Active Days</Label>
                    <Value>
                      {formatValue(refundEligibility.requirements?.active_days?.current)}
                      {' / '}
                      {formatValue(refundEligibility.requirements?.active_days?.required)}
                    </Value>
                  </Row>
                  <Row>
                    <Label>Mutual Matches During MBG Period</Label>
                    <Value>
                      {formatValue(refundEligibility.requirements?.matches_received?.current)}
                    </Value>
                  </Row>
                  <Row>
                    <Label>Profile Completion Achieved</Label>
                    <Value>{formatValue(refundEligibility.requirements?.profile_completion?.current)}</Value>
                  </Row>
                  <Row>
                    <Label>Checked On</Label>
                    <Value>{formatValue(refundEligibility.checked_at)}</Value>
                  </Row>
                </Grid>

                <Description style={{ marginTop: '14px', marginBottom: 0 }}>
                  Matches here mean full mutual accepts from both users, not one-sided likes.
                </Description>
              </Section>
            ) : null}

            {hasMeaningfulValue(mbgInfo?.current_subscription_data) ? (
              <Section>
                <SectionTitle>Current Subscription Data</SectionTitle>
                <Row>
                  <Label>Current Subscription Data</Label>
                  <Value>{formatValue(mbgInfo?.current_subscription_data)}</Value>
                </Row>
              </Section>
            ) : null}

          </>
        )}
      </ParentDiv>
    </BaseModel>
  );
};

export default MoneyBackGuaranteeModal;
